import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  IconButton,
  ListItemText,
  MenuItem,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  FilterAltOff,
  Description,
  Print,
  UnfoldLess,
  UnfoldMore,
  Visibility,
  VisibilityOff,
} from "@mui/icons-material";
import HeaderInternal from "../components/Header/HeaderInternal";
import { FooterInternal } from "../components/Footer";
import { FaqButton } from "../components/FaqButton";
import ReportSummaryCard, { summaryCardsRowSx } from "../components/Reports/ReportSummaryCard";
import MonthSelect from "../components/Inputs/MonthSelect";
import { useRouter } from "../contexts/RouterContext";
import { useAuth } from "../contexts/AuthContext";
import { colors } from "../theme/designSystem";
import { filterFieldSx } from "../theme/filterStyles";
import { maskName } from "../utils/nameMask";
import {
  reportsService,
  ProfessionalProductivityItem,
  ProfessionalProductivityResponse,
  ProductivityServiceRow,
  ReportProfessionalItem,
} from "../services/reportsService";

// Produtividade e repasse por profissional (RN-215): a agenda cruzada com as evoluções.
// A pergunta da tela: quanto do repasse do período está sem registro clínico por trás.
// Mesma fonte, exclusões e régua de presença da Análise de Atendimentos — os dois precisam bater.

const formatCurrency = (value: number): string =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

const formatMonthLabel = (month: string): string => {
  const [year, monthNumber] = month.split("-");
  const names = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];
  return `${names[parseInt(monthNumber, 10) - 1]}/${year.slice(2)}`;
};

const currentCompetence = (): string => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const competenceBounds = (competence: string): { from: string; to: string } => {
  const [year, month] = competence.split("-").map((v) => parseInt(v, 10));
  const lastDay = new Date(year, month, 0).getDate();
  const mm = String(month).padStart(2, "0");
  return { from: `${year}-${mm}-01`, to: `${year}-${mm}-${String(lastDay).padStart(2, "0")}` };
};

/** Verde a partir de 80%, laranja de 40 a 79, vermelho abaixo: a cor diz onde agir sem ler o número. */
const corDaAdesao = (pct: number): string => (pct >= 80 ? "#28a745" : pct >= 40 ? "#fd7e14" : "#dc3545");

const filterCardSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid #e9ecef",
  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
  padding: "1rem",
  marginBottom: "1.5rem",
};

const painelSx = {
  backgroundColor: "white",
  borderRadius: "8px",
  border: "1px solid #e9ecef",
  boxShadow: "0 1px 4px rgba(0, 0, 0, 0.04)",
  padding: "1rem 1.25rem",
};

const clearFilterButtonSx = {
  width: 40,
  height: 40,
  border: "1px solid #ced4da",
  borderRadius: "4px",
  color: colors.primary,
};

/**
 * As colunas em um lugar só: cabeçalho, linha de serviço, subtotal e total leem daqui. Antes de
 * centralizar, cada nível declarava a própria largura e a tabela saía de alinhamento.
 * `numero` marca o que é somável — é o que decide o que aparece nas linhas de subtotal e total.
 */
const COLUNAS: { titulo: string; largura: string; numero: boolean; moeda?: boolean }[] = [
  { titulo: "Profissional / serviço", largura: "1 1 240px", numero: false },
  { titulo: "Agend.", largura: "0 0 70px", numero: true },
  { titulo: "Canc. pac.", largura: "0 0 80px", numero: true },
  { titulo: "Canc. prof.", largura: "0 0 80px", numero: true },
  { titulo: "Compar.", largura: "0 0 75px", numero: true },
  { titulo: "Evoluções", largura: "0 0 80px", numero: true },
  { titulo: "% evoluído", largura: "0 0 85px", numero: false },
  { titulo: "Repasse (compar.)", largura: "0 0 120px", numero: true, moeda: true },
  { titulo: "Repasse (evoluído)", largura: "0 0 120px", numero: true, moeda: true },
  { titulo: "Sem registro", largura: "0 0 115px", numero: true, moeda: true },
];

const celulaSx = (i: number) => ({
  flex: COLUNAS[i].largura,
  minWidth: 0,
  textAlign: (i === 0 ? "left" : "right") as "left" | "right",
  fontVariantNumeric: "tabular-nums" as const,
});

/** Barra de duas camadas: o total é o repasse comparecido; o preenchido é a parte evoluída. */
const BarraDeRepasse: React.FC<{ total: number; parte: number; maximo: number }> = ({ total, parte, maximo }) => {
  const larguraTotal = maximo > 0 ? (100 * total) / maximo : 0;
  const larguraParte = total > 0 ? (100 * parte) / total : 0;
  return (
    <Box sx={{ width: `${larguraTotal}%`, minWidth: 2, height: 14, borderRadius: 3, backgroundColor: "#f1d0d0", overflow: "hidden" }}>
      <Box sx={{ width: `${larguraParte}%`, height: "100%", backgroundColor: "#0d6e6e", transition: "width .3s" }} />
    </Box>
  );
};

const ReportProfessionalProductivity: React.FC = () => {
  const { navigateTo } = useRouter();
  const { user } = useAuth();

  const [competence, setCompetence] = useState(currentCompetence());
  const [fromDate, setFromDate] = useState(competenceBounds(currentCompetence()).from);
  const [toDate, setToDate] = useState(competenceBounds(currentCompetence()).to);
  const [selectedProfessionals, setSelectedProfessionals] = useState<string[]>([]);
  const [showInactiveProfessionals, setShowInactiveProfessionals] = useState(false);
  const [showNames, setShowNames] = useState(false);

  const [professionals, setProfessionals] = useState<ReportProfessionalItem[]>([]);
  const visibleProfessionals = professionals.filter((p) => (p.active ?? true) || showInactiveProfessionals);
  const [data, setData] = useState<ProfessionalProductivityResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [abertos, setAbertos] = useState<string[]>([]);

  const displayName = (name: string): string => (showNames ? name : maskName(name));

  const defaultBounds = competenceBounds(currentCompetence());
  const hasFilterChanges =
    competence !== currentCompetence() ||
    fromDate !== defaultBounds.from ||
    toDate !== defaultBounds.to ||
    selectedProfessionals.length > 0;

  const loadData = useCallback(
    async (override?: { from: string; to: string; professionalIds: string[] }) => {
      const params = override ?? { from: fromDate, to: toDate, professionalIds: selectedProfessionals };
      if (!params.from || !params.to) return;
      setLoading(true);
      setError("");
      try {
        setData(await reportsService.getProfessionalProductivity(params));
      } catch {
        setError("Não foi possível carregar os dados. Tente novamente.");
        setData(null);
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, selectedProfessionals],
  );

  const handleCompetenceChange = (value: string) => {
    setCompetence(value);
    if (value) {
      const bounds = competenceBounds(value);
      setFromDate(bounds.from);
      setToDate(bounds.to);
      loadData({ from: bounds.from, to: bounds.to, professionalIds: selectedProfessionals });
    }
  };

  const handleFromDateChange = (value: string) => {
    setFromDate(value);
    setCompetence("");
    if (value && toDate) loadData({ from: value, to: toDate, professionalIds: selectedProfessionals });
  };

  const handleToDateChange = (value: string) => {
    setToDate(value);
    setCompetence("");
    if (fromDate && value) loadData({ from: fromDate, to: value, professionalIds: selectedProfessionals });
  };

  const handleProfessionalsChange = (ids: string[]) => {
    setSelectedProfessionals(ids);
    loadData({ from: fromDate, to: toDate, professionalIds: ids });
  };

  const toggleInactiveProfessionals = () => {
    setShowInactiveProfessionals((prev) => {
      const next = !prev;
      if (!next) {
        const onlyActive = selectedProfessionals.filter(
          (id) => professionals.find((p) => p.externalId === id)?.active ?? true,
        );
        if (onlyActive.length !== selectedProfessionals.length) {
          handleProfessionalsChange(onlyActive);
        }
      }
      return next;
    });
  };

  const handleSelectAllProfessionals = () => {
    const ids =
      selectedProfessionals.length === visibleProfessionals.length ? [] : visibleProfessionals.map((p) => p.externalId);
    handleProfessionalsChange(ids);
  };

  const clearFilters = () => {
    const bounds = competenceBounds(currentCompetence());
    setCompetence(currentCompetence());
    setFromDate(bounds.from);
    setToDate(bounds.to);
    setSelectedProfessionals([]);
    loadData({ from: bounds.from, to: bounds.to, professionalIds: [] });
  };

  /**
   * Impressão analítica em página própria (RN-215e): um profissional por folha, com os
   * atendimentos realizados. Vai em nova aba levando o recorte atual — de lá dá para apertar o
   * filtro sem voltar.
   */
  const abrirImpressaoPorProfissional = () => {
    const clinic = new URLSearchParams(window.location.search).get("clinic") || "ninho";
    const params = new URLSearchParams({ page: "report-productivity-print", clinic, from: fromDate, to: toDate });
    if (competence) params.set("competence", competence);
    if (selectedProfessionals.length > 0) params.set("professionals", selectedProfessionals.join(","));
    window.open(`${window.location.origin}/?${params.toString()}`, "_blank");
  };

  const handlePrint = () => {
    // impressão sempre com nomes mascarados, como nos demais relatórios
    const wasShowing = showNames;
    setShowNames(false);
    setTimeout(() => {
      window.print();
      setShowNames(wasShowing);
    }, 150);
  };

  useEffect(() => {
    reportsService.getProfessionals().then(setProfessionals).catch(() => setProfessionals([]));
  }, []);

  useEffect(() => {
    loadData();
    // carga inicial apenas; as demais saem dos filtros reativos
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const itens = data?.items ?? [];
  const todosAbertos = itens.length > 0 && abertos.length === itens.length;

  const maxRepasse = useMemo(
    () => Math.max(1, ...itens.map((i) => i.payoutAttended)),
    [itens],
  );
  const maxMes = useMemo(
    () => Math.max(1, ...(data?.monthly.map((m) => m.payoutAttended) ?? [1])),
    [data],
  );

  /** Pior adesão primeiro: a lista existe para agir, e quem já está em 100% não precisa de atenção. */
  const piores = useMemo(
    () => [...itens].filter((i) => i.attended > 0).sort((a, b) => a.evolvedRate - b.evolvedRate).slice(0, 8),
    [itens],
  );

  const semRegistro = (linha: { payoutAttended: number; payoutEvolved: number }): number =>
    linha.payoutAttended - linha.payoutEvolved;

  const alternar = (id: string) =>
    setAbertos((atual) => (atual.includes(id) ? atual.filter((x) => x !== id) : [...atual, id]));

  const cards = data
    ? [
        { label: "Agendamentos", value: String(data.totals.totalSchedules) },
        { label: "Cancel. paciente", value: String(data.totals.cancelledByPatient), color: "#fd7e14" },
        { label: "Cancel. profissional", value: String(data.totals.cancelledByProfessional), color: "#6f42c1" },
        {
          label: "Comparecidos",
          value: String(data.totals.attended),
          color: "#28a745",
          sub: `${data.totals.attendanceRate}% da agenda`,
        },
        {
          label: "Evoluções",
          value: String(data.totals.evolved),
          color: corDaAdesao(data.totals.evolvedRate),
          sub: `${data.totals.evolvedRate}% dos comparecidos`,
          subColor: corDaAdesao(data.totals.evolvedRate),
        },
        { label: "Repasse (compareceu)", value: formatCurrency(data.totals.payoutAttended), color: colors.primary },
        { label: "Repasse (evoluído)", value: formatCurrency(data.totals.payoutEvolved), color: "#0d6e6e" },
        {
          label: "Repasse sem registro",
          value: formatCurrency(semRegistro(data.totals)),
          color: semRegistro(data.totals) > 0 ? "#dc3545" : "#adb5bd",
          note: "sessões pagas sem evolução",
        },
      ]
    : [];

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column", backgroundColor: "#f8f9fa" }}>
      <HeaderInternal
        showCTAButton={false}
        className="login-header"
        isLoggedIn={true}
        userEmail={user?.email || ""}
        userProfile={user?.role || ""}
        clinicName={user?.clinicName || "Clínica"}
        notificationCount={0}
        onRevalidateLogin={() => {}}
        onNotificationClick={() => {}}
        onUserClick={() => {}}
        onLogoClick={() => navigateTo("dashboard")}
      />

      <Box component="main" className="c4u-main">
        <Container maxWidth={false} disableGutters>
          <Box className="c4u-page-header c4u-page-header--in-panel">
            <Box>
              <Typography variant="h4" className="c4u-page-header__title">
                Produtividade e repasse
              </Typography>
              <Typography variant="body2" className="c4u-page-header__subtitle">
                Quanto do repasse do período tem evolução registrada por trás. Mesma base e mesmas
                regras da Análise de Atendimentos.
              </Typography>
            </Box>
            <Box className="no-print">
              <FaqButton />
            </Box>
          </Box>

          <div className="c4u-panel">
            <Paper className="no-print" elevation={0} sx={filterCardSx}>
              <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: "0.75rem", mb: 1.5 }}>
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
                    gap: "0.75rem",
                    flex: 1,
                  }}
                >
                  <MonthSelect label="Competência" value={competence} onChange={handleCompetenceChange} sx={filterFieldSx} />
                  <TextField
                    size="small"
                    type="date"
                    label="De"
                    value={fromDate}
                    onChange={(e) => handleFromDateChange(e.target.value)}
                    fullWidth
                    sx={filterFieldSx}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    size="small"
                    type="date"
                    label="Até"
                    value={toDate}
                    onChange={(e) => handleToDateChange(e.target.value)}
                    fullWidth
                    sx={filterFieldSx}
                    InputLabelProps={{ shrink: true }}
                  />
                  <TextField
                    select
                    size="small"
                    label="Profissionais"
                    value={selectedProfessionals}
                    onChange={(e) => {
                      const value = e.target.value;
                      const ids = (typeof value === "string" ? value.split(",") : value).filter((v) => v !== "all");
                      handleProfessionalsChange(ids);
                    }}
                    SelectProps={{
                      multiple: true,
                      renderValue: (selected) => {
                        const ids = selected as string[];
                        if (ids.length === 0) return "Todos";
                        if (ids.length > 2) return `${ids.length} selecionados`;
                        return professionals
                          .filter((p) => ids.includes(p.externalId))
                          .map((p) => displayName(p.name))
                          .join(", ");
                      },
                      displayEmpty: true,
                    }}
                    fullWidth
                    sx={filterFieldSx}
                    InputLabelProps={{ shrink: true }}
                  >
                    <MenuItem
                      value="all"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        handleSelectAllProfessionals();
                      }}
                      sx={{
                        backgroundColor: "#f8f9fa !important",
                        fontWeight: 600,
                        fontSize: "0.85rem",
                        borderBottom: "1px solid #e9ecef",
                      }}
                    >
                      <Checkbox
                        checked={visibleProfessionals.length > 0 && selectedProfessionals.length === visibleProfessionals.length}
                        indeterminate={
                          selectedProfessionals.length > 0 && selectedProfessionals.length < visibleProfessionals.length
                        }
                        sx={{ padding: "4px 8px 4px 4px", "& .MuiSvgIcon-root": { fontSize: "1.2rem" } }}
                      />
                      <ListItemText
                        primary={`Selecionar Todos (${visibleProfessionals.length})`}
                        primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 600 }}
                      />
                    </MenuItem>
                    {/* Inativos sob demanda, como na Agenda: histórico de quem saiu */}
                    <MenuItem
                      value="__inactive"
                      onMouseDown={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleInactiveProfessionals();
                      }}
                      sx={{
                        backgroundColor: "#f8f9fa !important",
                        fontSize: "0.85rem",
                        borderBottom: "1px solid #e9ecef",
                      }}
                    >
                      <Checkbox
                        checked={showInactiveProfessionals}
                        sx={{ padding: "4px 8px 4px 4px", "& .MuiSvgIcon-root": { fontSize: "1.2rem" } }}
                      />
                      <ListItemText primary="Mostrar inativos" primaryTypographyProps={{ fontSize: "0.85rem", fontWeight: 500 }} />
                    </MenuItem>
                    {visibleProfessionals.map((p) => (
                      <MenuItem key={p.externalId} value={p.externalId}>
                        <Checkbox size="small" checked={selectedProfessionals.includes(p.externalId)} />
                        <ListItemText primary={displayName(p.name) + (p.active === false ? " (inativo)" : "")} primaryTypographyProps={{ fontSize: 14 }} />
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
                <IconButton
                  onClick={clearFilters}
                  disabled={!hasFilterChanges || loading}
                  title="Limpar filtros"
                  sx={{ ...clearFilterButtonSx, flexShrink: 0, alignSelf: { xs: "flex-end", sm: "auto" } }}
                >
                  <FilterAltOff fontSize="small" />
                </IconButton>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  flexDirection: { xs: "column", sm: "row" },
                  justifyContent: { sm: "flex-end" },
                  gap: "0.5rem",
                }}
              >
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={showNames ? <VisibilityOff /> : <Visibility />}
                  onClick={() => setShowNames((v) => !v)}
                >
                  {showNames ? "Ocultar nomes" : "Exibir nomes"}
                </Button>
                <Button size="small" variant="outlined" startIcon={<Print />} onClick={handlePrint}>
                  Imprimir painel
                </Button>
                <Button size="small" variant="outlined" startIcon={<Description />} onClick={abrirImpressaoPorProfissional}>
                  Um profissional por folha
                </Button>
              </Box>
            </Paper>

            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 8 }}>
                <CircularProgress size={28} />
              </Box>
            ) : error ? (
              <Typography sx={{ p: 3, fontSize: 14, color: "#dc3545" }}>{error}</Typography>
            ) : !data || itens.length === 0 ? (
              <Typography sx={{ p: 3, fontSize: 14, color: "#6c757d" }}>
                Nenhum atendimento no período com os filtros aplicados.
              </Typography>
            ) : (
              <>
                <Box sx={summaryCardsRowSx}>
                  {cards.map((c) => (
                    <ReportSummaryCard key={c.label} {...c} />
                  ))}
                </Box>

                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr", lg: "3fr 2fr" },
                    gap: 2,
                    mb: 3,
                  }}
                >
                  <Paper elevation={0} sx={painelSx}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Repasse por profissional</Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#adb5bd", mb: 1.5 }}>
                      A barra inteira é o repasse por comparecimento; a parte escura é a que tem evolução
                      registrada. O vermelho que sobra é o que está sem registro.
                    </Typography>
                    {itens.slice(0, 12).map((i) => (
                      <Box key={i.professionalExternalId || i.professionalName} sx={{ mb: 1.1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1, mb: 0.3 }}>
                          <Typography sx={{ fontSize: 12, color: "#495057", overflowWrap: "anywhere" }}>
                            {displayName(i.professionalName)}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: "#6c757d", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                            {formatCurrency(i.payoutEvolved)} de {formatCurrency(i.payoutAttended)}
                          </Typography>
                        </Box>
                        <BarraDeRepasse total={i.payoutAttended} parte={i.payoutEvolved} maximo={maxRepasse} />
                      </Box>
                    ))}
                  </Paper>

                  <Paper elevation={0} sx={painelSx}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Menor adesão</Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#adb5bd", mb: 1.5 }}>
                      Percentual das sessões que aconteceram e foram evoluídas. Pior primeiro — quem já
                      está em dia não precisa de atenção.
                    </Typography>
                    {piores.map((i) => (
                      <Box key={i.professionalExternalId || i.professionalName} sx={{ mb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                          <Typography sx={{ fontSize: 12, color: "#495057", overflowWrap: "anywhere" }}>
                            {displayName(i.professionalName)}
                          </Typography>
                          <Typography
                            sx={{ fontSize: 12, fontWeight: 700, color: corDaAdesao(i.evolvedRate), fontVariantNumeric: "tabular-nums" }}
                          >
                            {i.evolvedRate}%
                          </Typography>
                        </Box>
                        <Box sx={{ height: 5, borderRadius: 3, backgroundColor: "#f1f3f5", overflow: "hidden" }}>
                          <Box
                            sx={{
                              width: `${i.evolvedRate}%`,
                              height: "100%",
                              backgroundColor: corDaAdesao(i.evolvedRate),
                              transition: "width .3s",
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                </Box>

                {/* ================= TABELA ================= */}
                <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }} className="no-print">
                  <Button
                    size="small"
                    startIcon={todosAbertos ? <UnfoldLess /> : <UnfoldMore />}
                    onClick={() => setAbertos(todosAbertos ? [] : itens.map((i) => i.professionalExternalId || i.professionalName))}
                  >
                    {todosAbertos ? "Recolher todos" : "Abrir por serviço"}
                  </Button>
                </Box>

                <Box
                  sx={{
                    backgroundColor: "#fff",
                    borderRadius: "8px",
                    border: "1px solid #e2e8f0",
                    overflowX: "auto",
                    mb: 3,
                  }}
                >
                  {/* Soma das larguras das colunas + folga: abaixo disso a coluna do nome é
                      espremida e "Sessão de atendimento" quebra em três linhas no celular. */}
                  <Box sx={{ minWidth: 1120 }}>
                    <Box
                      sx={{
                        display: "flex",
                        gap: 1,
                        px: 1.5,
                        py: 1,
                        borderBottom: "2px solid #e2e8f0",
                        backgroundColor: "#f8f9fa",
                      }}
                    >
                      {COLUNAS.map((c, i) => (
                        <Typography key={c.titulo} sx={{ ...celulaSx(i), fontSize: 11.5, fontWeight: 700, color: "#495057" }}>
                          {c.titulo}
                        </Typography>
                      ))}
                    </Box>

                    {itens.map((item) => {
                      const chave = item.professionalExternalId || item.professionalName;
                      const aberto = abertos.includes(chave);
                      return (
                        <React.Fragment key={chave}>
                          {/* O profissional ABRE o grupo e os serviços vêm recuados abaixo dele.
                              Com o subtotal por último, os serviços pareciam pertencer ao
                              profissional da linha de cima (achado do Hilton, 10/09/2026). */}
                          <LinhaDeSubtotal
                            item={item}
                            nome={displayName(item.professionalName)}
                            aberto={aberto}
                            onToggle={() => alternar(chave)}
                          />
                          {aberto &&
                            item.services.map((s) => (
                              <LinhaDeServico key={`${chave}-${s.serviceName}`} linha={s} />
                            ))}
                        </React.Fragment>
                      );
                    })}

                    <LinhaDeTotal totals={data.totals} />
                  </Box>
                </Box>

                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 2 }}>
                  <Paper elevation={0} sx={painelSx}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Adesão mês a mês</Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#adb5bd", mb: 1.5 }}>
                      Um período de um mês só mostra uma barra — amplie De/Até para ver a tendência.
                    </Typography>
                    {(data.monthly ?? []).map((m) => {
                      const pct = m.attended > 0 ? Math.round((100 * m.evolved) / m.attended) : 0;
                      return (
                        <Box key={m.month} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 0.9 }}>
                          <Typography sx={{ fontSize: 11.5, color: "#6c757d", width: 52, flexShrink: 0 }}>
                            {formatMonthLabel(m.month)}
                          </Typography>
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <BarraDeRepasse total={m.payoutAttended} parte={m.payoutEvolved} maximo={maxMes} />
                          </Box>
                          <Typography
                            sx={{ fontSize: 11.5, fontWeight: 700, color: corDaAdesao(pct), width: 40, textAlign: "right", fontVariantNumeric: "tabular-nums" }}
                          >
                            {pct}%
                          </Typography>
                        </Box>
                      );
                    })}
                  </Paper>

                  <Paper elevation={0} sx={painelSx}>
                    <Typography sx={{ fontSize: 13, fontWeight: 700 }}>Por tipo de serviço</Typography>
                    <Typography sx={{ fontSize: 11.5, color: "#adb5bd", mb: 1.5 }}>
                      Somando todos os profissionais — mostra qual serviço concentra o problema.
                    </Typography>
                    {(data.byService ?? []).slice(0, 8).map((s) => (
                      <Box key={s.serviceName} sx={{ mb: 1 }}>
                        <Box sx={{ display: "flex", justifyContent: "space-between", gap: 1 }}>
                          <Typography sx={{ fontSize: 12, color: "#495057", overflowWrap: "anywhere" }}>
                            {s.serviceName}
                          </Typography>
                          <Typography sx={{ fontSize: 11.5, color: "#6c757d", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums" }}>
                            {s.evolved}/{s.attended} ·{" "}
                            <Typography component="span" sx={{ fontSize: 11.5, fontWeight: 700, color: corDaAdesao(s.evolvedRate) }}>
                              {s.evolvedRate}%
                            </Typography>
                          </Typography>
                        </Box>
                        <Box sx={{ height: 5, borderRadius: 3, backgroundColor: "#f1f3f5", overflow: "hidden" }}>
                          <Box
                            sx={{
                              width: `${s.evolvedRate}%`,
                              height: "100%",
                              backgroundColor: corDaAdesao(s.evolvedRate),
                              transition: "width .3s",
                            }}
                          />
                        </Box>
                      </Box>
                    ))}
                  </Paper>
                </Box>
              </>
            )}
          </div>
        </Container>
      </Box>

      <FooterInternal className="login-footer-component" />
    </Box>
  );
};

/** Linha do tipo de serviço: o nível mais fino, recuado sob o profissional. */
const LinhaDeServico: React.FC<{ linha: ProductivityServiceRow }> = ({ linha }) => {
  const semRegistro = linha.payoutAttended - linha.payoutEvolved;
  const valores = [
    linha.totalSchedules,
    linha.cancelledByPatient,
    linha.cancelledByProfessional,
    linha.attended,
    linha.evolved,
  ];
  return (
    <Box sx={{ display: "flex", gap: 1, px: 1.5, py: 0.6, borderBottom: "1px solid #f1f3f5", backgroundColor: "#fcfdfe" }}>
      <Typography sx={{ ...celulaSx(0), fontSize: 12, color: "#6c757d", pl: 2, overflowWrap: "anywhere" }}>
        {linha.serviceName}
      </Typography>
      {valores.map((v, i) => (
        <Typography key={COLUNAS[i + 1].titulo} sx={{ ...celulaSx(i + 1), fontSize: 12, color: v > 0 ? "#495057" : "#ced4da" }}>
          {v}
        </Typography>
      ))}
      <Typography sx={{ ...celulaSx(6), fontSize: 12, fontWeight: 600, color: corDaAdesao(linha.evolvedRate) }}>
        {linha.attended > 0 ? `${linha.evolvedRate}%` : "—"}
      </Typography>
      <Typography sx={{ ...celulaSx(7), fontSize: 12, color: "#495057" }}>
        {formatCurrency(linha.payoutAttended)}
      </Typography>
      <Typography sx={{ ...celulaSx(8), fontSize: 12, color: "#0d6e6e" }}>
        {formatCurrency(linha.payoutEvolved)}
      </Typography>
      <Typography sx={{ ...celulaSx(9), fontSize: 12, color: semRegistro > 0 ? "#dc3545" : "#ced4da" }}>
        {formatCurrency(semRegistro)}
      </Typography>
    </Box>
  );
};

/**
 * Linha do profissional: encabeça o grupo E é o subtotal dele. Soma quantidades e valores das
 * linhas de serviço que vêm abaixo, e traz o percentual RECALCULADO sobre o total do nível
 * (evoluções do profissional ÷ comparecidos do profissional) — nunca a soma nem a média dos
 * percentuais das linhas, em que uma de 1 sessão pesaria o mesmo que uma de 60.
 */
const LinhaDeSubtotal: React.FC<{
  item: ProfessionalProductivityItem;
  nome: string;
  aberto: boolean;
  onToggle: () => void;
}> = ({ item, nome, aberto, onToggle }) => {
  const semRegistro = item.payoutAttended - item.payoutEvolved;
  const valores = [
    item.totalSchedules,
    item.cancelledByPatient,
    item.cancelledByProfessional,
    item.attended,
    item.evolved,
  ];
  return (
    <Box
      onClick={onToggle}
      sx={{
        display: "flex",
        gap: 1,
        px: 1.5,
        py: 0.9,
        borderBottom: "1px solid #e2e8f0",
        backgroundColor: aberto ? "#f1f7f7" : "#fff",
        cursor: "pointer",
        "&:hover": { backgroundColor: "#f8fbfb" },
      }}
    >
      <Box sx={{ ...celulaSx(0), display: "flex", alignItems: "center", gap: 0.5 }}>
        <Tooltip title={aberto ? "Recolher serviços" : "Ver por tipo de serviço"} arrow>
          <Box sx={{ display: "flex", color: "#6c757d" }}>
            {aberto ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
          </Box>
        </Tooltip>
        <Typography sx={{ fontSize: 13, fontWeight: 700, overflowWrap: "anywhere" }}>{nome}</Typography>
      </Box>
      {valores.map((v, i) => (
        <Typography key={COLUNAS[i + 1].titulo} sx={{ ...celulaSx(i + 1), fontSize: 13, fontWeight: 700 }}>
          {v}
        </Typography>
      ))}
      <Typography sx={{ ...celulaSx(6), fontSize: 13, fontWeight: 700, color: corDaAdesao(item.evolvedRate) }}>
        {item.attended > 0 ? `${item.evolvedRate}%` : "—"}
      </Typography>
      <Typography sx={{ ...celulaSx(7), fontSize: 13, fontWeight: 700 }}>
        {formatCurrency(item.payoutAttended)}
      </Typography>
      <Typography sx={{ ...celulaSx(8), fontSize: 13, fontWeight: 700, color: "#0d6e6e" }}>
        {formatCurrency(item.payoutEvolved)}
      </Typography>
      <Typography sx={{ ...celulaSx(9), fontSize: 13, fontWeight: 700, color: semRegistro > 0 ? "#dc3545" : "#adb5bd" }}>
        {formatCurrency(semRegistro)}
      </Typography>
    </Box>
  );
};

/** Total geral, pela mesma regra do subtotal: o percentual é recalculado sobre o total do período. */
const LinhaDeTotal: React.FC<{
  totals: {
    totalSchedules: number;
    cancelledByPatient: number;
    cancelledByProfessional: number;
    attended: number;
    evolved: number;
    payoutAttended: number;
    payoutEvolved: number;
    evolvedRate: number;
  };
}> = ({ totals }) => {
  const semRegistro = totals.payoutAttended - totals.payoutEvolved;
  const valores = [
    totals.totalSchedules,
    totals.cancelledByPatient,
    totals.cancelledByProfessional,
    totals.attended,
    totals.evolved,
  ];
  return (
    <Box sx={{ display: "flex", gap: 1, px: 1.5, py: 1.1, borderTop: "2px solid #0d6e6e", backgroundColor: "#f1f7f7" }}>
      <Typography sx={{ ...celulaSx(0), fontSize: 13, fontWeight: 700, color: "#0d6e6e" }}>TOTAL</Typography>
      {valores.map((v, i) => (
        <Typography key={COLUNAS[i + 1].titulo} sx={{ ...celulaSx(i + 1), fontSize: 13, fontWeight: 700, color: "#0d6e6e" }}>
          {v}
        </Typography>
      ))}
      <Typography sx={{ ...celulaSx(6), fontSize: 13, fontWeight: 700, color: corDaAdesao(totals.evolvedRate) }}>
        {totals.attended > 0 ? `${totals.evolvedRate}%` : "—"}
      </Typography>
      <Typography sx={{ ...celulaSx(7), fontSize: 13, fontWeight: 700, color: "#0d6e6e" }}>
        {formatCurrency(totals.payoutAttended)}
      </Typography>
      <Typography sx={{ ...celulaSx(8), fontSize: 13, fontWeight: 700, color: "#0d6e6e" }}>
        {formatCurrency(totals.payoutEvolved)}
      </Typography>
      <Typography sx={{ ...celulaSx(9), fontSize: 13, fontWeight: 700, color: semRegistro > 0 ? "#dc3545" : "#adb5bd" }}>
        {formatCurrency(semRegistro)}
      </Typography>
    </Box>
  );
};

export default ReportProfessionalProductivity;
